import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchDolibarrOrders,
  fetchOrdersByProductType,
  createDolibarrInvoice,
} from '../components/DolibarrOrderService';

/* ------------------------------- Utils ---------------------------------- */

const numberify = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const isProduct = (line) => numberify(line?.product_type) === 0;
const productName = (line) =>
  line?.product_label || line?.product_ref || line?.desc || line?.label || 'Produit';

const toMonthYear = (ts) => {
  const d = new Date(numberify(ts) * 1000);
  if (Number.isNaN(d.getTime())) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const y = d.getFullYear();
  return `${m}/${y}`;
};

const parseMonthYear = (input) => {
  if (input == null) return '';

  if (typeof input === 'number') {
    const secs = input > 1e12 ? input / 1000 : input; // ms -> s
    return toMonthYear(secs);
  }

  if (typeof input === 'string') {
    const mmYYYY = input.match(/(\d{2})\/(\d{4})/);
    if (mmYYYY) return `${mmYYYY[1]}/${mmYYYY[2]}`;
    const dmy = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);         // DD/MM/YYYY
    if (dmy) return `${dmy[2]}/${dmy[3]}`;
    const ymd = input.match(/^(\d{4})-(\d{2})-(\d{2})/);            // YYYY-MM-DD
    if (ymd) return `${ymd[2]}/${ymd[1]}`;
    const d = new Date(input);
    if (!Number.isNaN(d.getTime())) {
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const y = d.getFullYear();
      return `${m}/${y}`;
    }
    return '';
  }

  const d = new Date(input);
  if (!Number.isNaN(d.getTime())) {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const y = d.getFullYear();
    return `${m}/${y}`;
  }
  return '';
};

// Canonical key: last segment after "_" of display label (note or canal), lowercased
const canonicalKey = (s) => {
  if (!s || typeof s !== 'string') return '';
  const i = s.lastIndexOf('_');
  const part = (i >= 0 ? s.slice(i + 1) : s).trim();
  return part.toLowerCase().replace(/\s+/g, ' ');
};
const displayLabelOf = (obj) =>
  (obj?.note_private && String(obj.note_private).trim()) ||
  (obj?.canal && String(obj.canal).trim()) ||
  'Sans note';
const canonicalKeyOf = (obj) => canonicalKey(displayLabelOf(obj));

const monthYearOfOrder = (order) =>
  parseMonthYear(order?.date || order?.date_creation || order?.orderDate);

const monthYearOfProcessed = (doc) => {
  const redev = (doc?.products || []).find((p) =>
    /^Redevance\s+\d{2}\/\d{4}\s*/i.test(p?.title || '')
  );
  if (redev) {
    const m = redev.title.match(/(\d{2}\/\d{4})/);
    if (m) return m[1];
  }
  return parseMonthYear(
    doc?.date || doc?.datef || doc?.date_creation || doc?.orderDate || doc?.createdAt
  );
};

const aggregateProducts = (lines) => {
  const products = {};
  let total = 0;
  lines?.forEach((line) => {
    if (!isProduct(line)) return;
    const name = productName(line);
    const qty = numberify(line?.qty);
    const amount = numberify(line?.total_ttc ?? line?.total ?? line?.total_with_tax);
    total += amount;
    if (!products[name]) products[name] = { qty: 0, total: 0 };
    products[name].qty += qty;
    products[name].total += amount;
  });
  return { products, total };
};

/* ----------------------------- Group builder ----------------------------- */
/** processedInfo = { [canon]: { [MM/YYYY]: { orderNumber, redevAmount, redevTitle } } } */
const buildMonths = (orders, processedInfo = {}) => {
  const months = {};
  orders?.forEach((order) => {
    const monthYear = monthYearOfOrder(order);
    if (!monthYear) return;

    const canon = canonicalKeyOf(order);
    const displayLabel = displayLabelOf(order);

    if (!months[monthYear]) months[monthYear] = {};
    if (!months[monthYear][canon]) {
      months[monthYear][canon] = {
        displayLabel,
        products: {},
        total: 0,
        processed: false,
        orderNumber: '',
        redevAmount: 0,
        redevTitle: '',
      };
    }

    const group = months[monthYear][canon];

    // mark processed but still aggregate products from raw orders
    const p = processedInfo[canon]?.[monthYear];
    if (p) {
      group.processed = true;
      group.orderNumber = p.orderNumber || '';
      group.redevAmount = numberify(p.redevAmount);                 // should already be negative
      if (group.redevAmount > 0) group.redevAmount = -Math.abs(group.redevAmount);
      group.redevTitle = p.redevTitle || `Redevance ${monthYear}`;
    }

    const { products, total } = aggregateProducts(order?.lines || []);
    group.total += total;
    Object.entries(products).forEach(([name, data]) => {
      if (!group.products[name]) group.products[name] = { qty: 0, total: 0 };
      group.products[name].qty += data.qty;
      group.products[name].total += data.total;
    });
  });
  return months;
};

/* ------------------------------- Component ------------------------------- */

const OrdersForCarrefourPage = () => {
  const [orders, setOrders] = useState([]);
  const [processedInfo, setProcessedInfo] = useState({}); // see shape above
  const [fees, setFees] = useState({}); // % per (month__canon) for NON-processed only
  const [activeMonth, setActiveMonth] = useState('');
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const load = async () => {
      try {
        const [ordersData, processedData] = await Promise.all([
          fetchDolibarrOrders(token),                          // raw orders (source for products)
          fetchOrdersByProductType('CarrefourGlobal', token),  // processed docs (contains Redevance line)
        ]);

        const list = Array.isArray(ordersData)
          ? ordersData
          : ordersData?.orders || ordersData?.data || [];
        setOrders(list);

        const processedList = Array.isArray(processedData)
          ? processedData
          : processedData?.orders || processedData?.data || [];

        // Build processedInfo with Redevance line (title starts with "Redevance")
        const info = {};
        processedList.forEach((doc) => {
          const canon = canonicalKeyOf(doc);
          const month = monthYearOfProcessed(doc);
          if (!canon || !month) return;

          const redevLine = (doc.products || []).find((p) =>
            /^Redevance\s+/i.test(p?.title || '')
          );
          if (!redevLine) return;

          const amount = numberify(redevLine.price);
          const negative = amount <= 0 ? amount : -Math.abs(amount);

          if (!info[canon]) info[canon] = {};
          info[canon][month] = {
            orderNumber: doc?.orderNumber || '',
            redevAmount: negative,
            redevTitle: redevLine.title || `Redevance ${month}`,
          };
        });
        setProcessedInfo(info);
      } catch (e) {
        console.error(e);
        setOrders([]);
        setProcessedInfo({});
      }
    };
    load();
  }, []);

  const months = useMemo(() => buildMonths(orders, processedInfo), [orders, processedInfo]);

  const monthKeys = useMemo(() => {
    return Object.keys(months).sort((a, b) => {
      const [ma, ya] = a.split('/'); // MM/YYYY
      const [mb, yb] = b.split('/');
      const ka = `${ya}-${ma}`;
      const kb = `${yb}-${mb}`;
      return kb.localeCompare(ka);
    });
  }, [months]);

  useEffect(() => {
    if (!activeMonth && monthKeys.length) setActiveMonth(monthKeys[0]);
  }, [monthKeys, activeMonth]);

  const formatter = useMemo(
    () => new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }),
    []
  );

  const monthToDate = (m) => {
    const [month, year] = m.split('/');
    return `${year}-${month}-01`;
  };

  const sendInvoice = async (month, canon, group) => {
    // only for NON-processed groups (processed groups already have a Redevance line)
    if (group.processed) return;

    const feeKey = `${month}__${canon}`;
    const pct = numberify(fees[feeKey]) || 0;
    if (pct <= 0) return;

    const redevance = -group.total * (pct / 100);

    const lines = Object.entries(group.products).map(([name, data]) => ({
      multicurrency_code: 'MAD',
      label: name,
      desc: name,
      tva_tx: 0,
      qty: data.qty,
      subprice: data.qty ? data.total / data.qty : 0,
      product_type: 0,
    }));

    const redevanceLabel = `Redevance ${month} ${pct}%`;
    lines.push({
      multicurrency_code: 'MAD',
      label: redevanceLabel,
      desc: redevanceLabel,
      tva_tx: 0,
      qty: 1,
      subprice: redevance,
      product_type: 1,
    });

    const date = monthToDate(month);
    const invoice = {
      socid: 272, // adapt if needed
      date,
      datef: date,
      note_private: group.displayLabel,
      lines,
    };

    try {
      const token = sessionStorage.getItem('token');
      const res = await createDolibarrInvoice(invoice, token);

      const ref =
        res?.ref || res?.invoice?.ref || res?.number || res?.orderNumber || res?.id || 'OK';

      // Mark as processed locally with the computed redevance
      setProcessedInfo((prev) => {
        const next = { ...prev };
        if (!next[canon]) next[canon] = {};
        next[canon][month] = {
          orderNumber: String(ref),
          redevAmount: redevance,
          redevTitle: redevanceLabel,
        };
        return next;
      });

      alert('Facture envoyée avec succès.');
    } catch (e) {
      console.error(e);
      alert("Échec d'envoi de la facture.");
    }
  };

  // Grand total excludes processed groups (to prevent double counting)
  const grandTotal = useMemo(
    () =>
      Object.entries(months).reduce((sum, [month, groups]) => {
        return (
          sum +
          Object.entries(groups).reduce((s, [canon, group]) => {
            if (group.processed) return s; // skip processed groups in grand total
            const feeKey = `${month}__${canon}`;
            const pct = numberify(fees[feeKey]) || 0;
            const redevance = -group.total * (pct / 100);
            return s + group.total + redevance;
          }, 0)
        );
      }, 0),
    [months, fees]
  );

  return (
    <div>
      <ul className="nav nav-tabs">
        {monthKeys.map((m) => (
          <li className="nav-item" key={m}>
            <a
              href="#"
              className={`nav-link ${activeMonth === m ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                setActiveMonth(m);
              }}
            >
              {m}
            </a>
          </li>
        ))}
      </ul>

      <div className="tab-content mt-2">
        {monthKeys.map((m) => (
          <div
            key={m}
            className={`tab-pane fade ${activeMonth === m ? 'show active' : ''}`}
          >
            <div className="list-group">
              {Object.entries(months[m] || {}).map(([canon, group]) => {
                const feeKey = `${m}__${canon}`;
                const pct = numberify(fees[feeKey]) || 0;
                const computedRedevence = -group.total * (pct / 100);
                const open = !!openGroups[feeKey];

                // If processed: use the real Redevance from processed docs
                const redevAmount = group.processed ? group.redevAmount : computedRedevence;
                const redevTitle = group.processed ? group.redevTitle : `Redevance ${m} ${pct}%`;
                const net = group.total + redevAmount;

                return (
                  <div className="list-group-item" key={feeKey}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <button
                          className="btn btn-link p-0"
                          onClick={() =>
                            setOpenGroups((prev) => ({ ...prev, [feeKey]: !prev[feeKey] }))
                          }
                        >
                          {open ? '▼' : '▶'} {group.displayLabel}
                        </button>

                        {group.processed ? (
                          <span className="ms-2">
                            • Déjà traité {group.orderNumber ? `(${group.orderNumber})` : ''}
                          </span>
                        ) : (
                          <>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={pct}
                              onChange={(e) =>
                                setFees((prev) => ({ ...prev, [feeKey]: numberify(e.target.value) }))
                              }
                              style={{ marginLeft: '0.5rem', width: '4rem' }}
                            />
                            %
                            {pct > 0 && (
                              <button
                                className="btn btn-sm btn-primary ms-2"
                                onClick={() => sendInvoice(m, canon, group)}
                              >
                                Envoyer à la facture
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {open && (
                      <div className="mt-2">
                        <table className="table table-striped table-sm">
                          <thead>
                            <tr>
                              <th>Produit</th>
                              <th>Quantité totale</th>
                              <th>Prix unitaire</th>
                              <th>Total TTC agrégé</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(group.products)
                              .sort(([, a], [, b]) => b.qty - a.qty)
                              .map(([name, data]) => (
                                <tr key={name}>
                                  <td>{name}</td>
                                  <td>{data.qty}</td>
                                  <td>{formatter.format(data.qty ? data.total / data.qty : 0)}</td>
                                  <td>{formatter.format(data.total)}</td>
                                </tr>
                              ))}

                            {/* If processed -> show real Redevance line (negative) from processed docs */}
                            {group.processed && (
                              <>
                                <tr>
                                  <td>{redevTitle}</td>
                                  <td colSpan={2}></td>
                                  <td>{formatter.format(redevAmount)}</td>
                                </tr>
                                <tr>
                                  <td><strong>Total net</strong></td>
                                  <td colSpan={2}></td>
                                  <td><strong>{formatter.format(net)}</strong></td>
                                </tr>
                              </>
                            )}

                            {/* If NOT processed and user chose a % -> preview computed redevance */}
                            {!group.processed && pct > 0 && (
                              <>
                                <tr>
                                  <td>{redevTitle}</td>
                                  <td colSpan={2}></td>
                                  <td>{formatter.format(redevAmount)}</td>
                                </tr>
                                <tr>
                                  <td><strong>Total net</strong></td>
                                  <td colSpan={2}></td>
                                  <td><strong>{formatter.format(net)}</strong></td>
                                </tr>
                              </>
                            )}
                          </tbody>
                        </table>

                        {/* If processed but no % field visible, also show aggregate total alone when needed */}
                        {pct === 0 && !group.processed && (
                          <p>
                            Total TTC agrégé : <strong>{formatter.format(group.total)}</strong>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p>
        Grand total : <strong>{formatter.format(grandTotal)}</strong>
      </p>
    </div>
  );
};

export default OrdersForCarrefourPage;
